import { SavedDatabaseModel } from '../saved/saved-database-model.type';
import { Link } from './link.class';
import { Table } from './table.class';

export class DatabaseModel {
  private _id?: number;
  private _title: string;
  private _tableList: Table[];
  private _linkList: Link[];
  private _userId?: number;

  constructor(title: string, tableList: Table[], tableLink: Link[]) {
    this._title = title;
    this._tableList = tableList;
    this._linkList = tableLink;
  }

  public static mapToSaved(model: DatabaseModel): SavedDatabaseModel {
    return {
      id: model.getId() as number,
      title: model.getTitle(),
      tableList: model.getTableList().map(table => Table.mapToSaved(table)),
      linkList: model.getLinkList().map(link => Link.mapToSaved(link)),
      userId: model._userId as number,
    };
  }

  public addTable(table: Table): DatabaseModel {
    this._tableList.push(table.setDatabaseModelId(this._id));
    return this;
  }

  public addLink(link: Link): DatabaseModel {
    this._linkList.push(link.setDatabaseModelId(this._id));
    return this;
  }

  public getId(): number {
    if (this._id === undefined) return -1;
    return this._id;
  }

  public setId(id: number | undefined): DatabaseModel {
    this._id = id;
    return this;
  }

  public getTitle(): string {
    return this._title;
  }

  public setTitle(newTitle: string): DatabaseModel {
    this._title = newTitle;
    return this;
  }

  public getUserId(): number | undefined {
    return this._userId;
  }

  public setUserId(userId: number | undefined): DatabaseModel {
    this._userId = userId;
    return this;
  }

  public getTableList(): Table[] {
    return this._tableList;
  }

  public setTableList(tableList: Table[]): DatabaseModel {
    this._tableList = tableList.map((table: Table) => table.setDatabaseModelId(this._id));
    return this;
  }

  public getLinkList(): Link[] {
    return this._linkList;
  }

  public setLinkList(linkList: Link[]): DatabaseModel {
    this._linkList = linkList.map((link: Link) => link.setDatabaseModelId(this._id));
    return this;
  }

  public isEmpty(): boolean {
    return !this._title && !this._tableList.length && !this._id && !this._linkList.length;
  }

  public deleteLink(linkToDelete: Link): DatabaseModel {
    const linkIndex = this._findInLinkList(linkToDelete);
    this._linkList.splice(linkIndex, 1);
    return this;
  }

  private _findInLinkList(linkToFind: Link): number {
    for (let i = 0; i < this._linkList.length; i += 1) {
      const link = this._linkList[i];
      if (link.table1 === linkToFind.table1 && link.table2 === linkToFind.table2) return i;
      if (link.table2 === linkToFind.table1 && link.table2 === linkToFind.table1) return i;
    }
    throw new Error('Link not found');
  }

  public deleteTable(tableToDelete: Table): DatabaseModel {
    const tableIndex = this._findInTableList(tableToDelete);
    this._tableList.splice(tableIndex, 1);
    this._deleteAllLinkWith(tableToDelete);
    return this;
  }

  private _findInTableList(tableToDelete: Table): number {
    for (let i = 0; i < this._tableList.length; i += 1) {
      const table = this._tableList[i];
      if (table.getTitle() === tableToDelete.getTitle()) return i;
    }
    throw new Error('Table not found');
  }

  private _deleteAllLinkWith(table: Table): void {
    const linkListToDelete = this._linkList.filter((link: Link) => link.table1 === table.getTitle() || link.table2 === table.getTitle());
    for (const link of linkListToDelete) {
      this.deleteLink(link);
    }
  }
}
